import { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components/macro'

import { Layout, Text } from '@loadsmart/miranda-react'
import { getToken, toCSSValue } from '@loadsmart/miranda-react/dist/tokens'

import {
  type Coordinate,
  Map,
  type MapStyle,
  type Marker,
  type TooltipMarker,
} from 'core/ui/components/map/Map'
import {
  DEFAULT_MARKER_ICON,
  DELIVERY_MARKER_ICON,
  IN_TRANSIT_CHECK_ICON,
  TRUCK_MARKER_ICON,
} from 'core/ui/components/map/Map.styles'
import { useGoogleMaps } from 'core/ui/hooks/useGoogleMaps'
import { getGeocode } from 'core/use-cases/getGeocode'
import { differenceInHours, parseISO } from 'core/utils/date'
import { parseNumber } from 'core/utils/number'

import formatZonedDate from 'legacy/utils/date/formatZonedDate'
import { formatCityName } from 'legacy/utils/formatCityName'

const TRUCK_ICON_LIMIT = 4

const Wrapper = styled.div`
  height: 100%;
  border: 1px solid ${getToken('color-neutral-100')};
`

const defaultContainerStyle: React.CSSProperties = {
  height: '425px',
  width: '100%',
}

export const inTransitCheckStyle: React.CSSProperties = {
  backgroundColor: toCSSValue('color-neutral-0'),
  borderRadius: getToken('border-radius-s'),
  boxShadow: `0 2px 4px 0 ${toCSSValue('color-neutral-100')}`,
  padding: getToken('spacing-2'),
  color: toCSSValue('color-neutral-100'),
  minWidth: '160px',
}

const stopsInfoBoxStyle: React.CSSProperties = {
  backgroundColor: toCSSValue('color-neutral-100'),
  borderRadius: getToken('border-radius-s'),
  padding: getToken('spacing-2'),
  minWidth: '160px',
}

export function getCoordinate({
  latitude,
  longitude,
}: LegacyShipmentStop | LegacyInTransitCheck): Coordinate {
  return {
    lat: parseNumber(latitude)!,
    lng: parseNumber(longitude)!,
  }
}

export function getMarkersFromStops(
  stops: Array<LegacyShipmentStop>,
  forceDefaultIcon = false
): Array<Marker> {
  let markers: Array<Marker> = []

  for (let i = 0; i < stops.length; i++) {
    let url
    let anchor

    if (i !== stops.length - 1) {
      url = DEFAULT_MARKER_ICON
    } else {
      url = forceDefaultIcon ? DEFAULT_MARKER_ICON : DELIVERY_MARKER_ICON

      if (!forceDefaultIcon) anchor = { x: 0, y: 25 }
    }

    markers.push({
      coordinate: getCoordinate(stops[i]),
      icon: {
        url,
        anchor,
      },
    })
  }
  return markers
}

export function getReportedLocationMarker(coordinate: Coordinate | null): Marker | undefined {
  if (!coordinate) return

  return {
    coordinate,
    icon: {
      url: TRUCK_MARKER_ICON,
      anchor: { x: 25, y: 25 },
      scaledSize: {
        height: 50,
        width: 50,
      },
    },
  }
}

export function getInTransitCheckMarker(inTransitCheck: LegacyInTransitCheck): Marker {
  return {
    icon: { url: IN_TRANSIT_CHECK_ICON },
    coordinate: getCoordinate(inTransitCheck),
  }
}

export function getInTransitTooltipMarker(
  legacyInTransitCheck: LegacyInTransitCheck
): TooltipMarker {
  const { city, state, timestamp, timezone } = legacyInTransitCheck

  const marker = getInTransitCheckMarker(legacyInTransitCheck)

  return {
    infoMarker: {
      coordinate: marker.coordinate,
      children: (
        <Layout.Stack gap="spacing-0-5">
          <Text color="color-neutral-100" variant="body-md-bold">
            {city}, {state}
          </Text>
          <Text color="color-neutral-100" variant="body-sm">
            {formatZonedDate(timestamp, timezone, 'EEE MM/dd/yy HH:mm')}
          </Text>
        </Layout.Stack>
      ),
      offset: {
        x: -80,
        y: -40,
      },
      style: inTransitCheckStyle,
    },
    marker,
  }
}

export function createStopInfoMarker(stop: LegacyShipmentStop) {
  const { window_start, window_end, timezone, city, state } = stop

  return {
    children: (
      <Layout.Stack gap="spacing-0-5">
        <Text color="color-neutral-0" variant="body-md-bold">
          {formatCityName(city)}, {state}
        </Text>
        <Text color="color-neutral-0" variant="body-sm">
          {formatZonedDate(window_start, timezone, 'MM/dd - KK:mm a')}
          {formatZonedDate(window_end, timezone, ' - KK:mm a')}
        </Text>
      </Layout.Stack>
    ),
    coordinate: getCoordinate(stop),
    offset: {
      x: -80,
      y: -40,
    },
    style: stopsInfoBoxStyle,
  }
}

export type LegacyShipmentStop = {
  longitude: string | number
  latitude: string | number
  city: string
  state: string
  window_start: string
  window_end: string
  timezone: string
}

export type LegacyShipment = {
  last_reported_location?: {
    location: string
    timestamp: string
  }
  stops: Array<LegacyShipmentStop>
}

export type LegacyInTransitCheck = {
  latitude: string
  longitude: string
  timestamp: string
  timezone: string
  city: string
  state: string
  id: string
}

export type MapContainerProps = {
  alternativeRouteColor?: string
  alternativeRouteStrokeWeight?: number
  enableZoomControl?: boolean
  forceDefaultMarkerIcon?: boolean
  inTransitChecks?: Array<LegacyInTransitCheck>
  mapStyles?: Array<MapStyle>
  mapContainerStyle?: React.CSSProperties
  primaryRouteColor?: string
  primaryRouteStrokeWeight?: number
  shipment: LegacyShipment
  showAlternativeRoutes?: boolean
  showMarkerTooltip?: boolean
}

export function MapContainer(props: MapContainerProps) {
  const {
    alternativeRouteColor,
    alternativeRouteStrokeWeight,
    enableZoomControl = true,
    forceDefaultMarkerIcon = false,
    inTransitChecks = [],
    mapStyles,
    mapContainerStyle = defaultContainerStyle,
    primaryRouteColor,
    primaryRouteStrokeWeight,
    shipment,
    showAlternativeRoutes = true,
    showMarkerTooltip = true,
  } = props

  const { isLoaded } = useGoogleMaps()

  const [reportedLocationMarker, setReportedLocationMarker] = useState<Marker | undefined>()
  const [truckIconLimit, setTruckIconLimit] = useState(0)

  const coordinates = shipment.stops.map(getCoordinate)
  const inTransitCheckToolTipMarkers = inTransitChecks.map(getInTransitTooltipMarker)
  const infoMarkers = shipment.stops.map(createStopInfoMarker)
  let defaultMarkers = getMarkersFromStops(shipment.stops, forceDefaultMarkerIcon)

  if (truckIconLimit < TRUCK_ICON_LIMIT && reportedLocationMarker) {
    defaultMarkers.push(reportedLocationMarker)
  }

  const setLastReportedLocation = useCallback(async () => {
    if (!isLoaded) return

    const { last_reported_location } = shipment
    if (!last_reported_location) return

    setReportedLocationMarker(undefined)

    const coordinate = await getGeocode(last_reported_location.location)
    const marker = getReportedLocationMarker(coordinate)

    setReportedLocationMarker(marker)
  }, [isLoaded, shipment])

  const setTruckIconTimeLimit = useCallback(async () => {
    const { last_reported_location } = shipment

    if (!last_reported_location) return

    const timeDifference = differenceInHours(new Date(), parseISO(last_reported_location.timestamp))
    setTruckIconLimit(timeDifference)
  }, [shipment])

  useEffect(() => {
    setLastReportedLocation()
    setTruckIconTimeLimit()
  }, [shipment, setLastReportedLocation, setTruckIconTimeLimit])

  if (!isLoaded || defaultMarkers.length < 2) return null

  return (
    <Wrapper>
      <Map
        alternativeRouteColor={alternativeRouteColor}
        alternativeRouteWeight={alternativeRouteStrokeWeight}
        coordinates={coordinates}
        enableZoomControl={enableZoomControl}
        infoMarkers={showMarkerTooltip ? infoMarkers : []}
        mapContainerStyle={mapContainerStyle}
        mapStyle={mapStyles}
        markers={defaultMarkers}
        primaryRouteColor={primaryRouteColor}
        primaryRouteWeight={primaryRouteStrokeWeight}
        showAlternativeRoutes={showAlternativeRoutes}
        tooltipMarkers={inTransitCheckToolTipMarkers}
      />
    </Wrapper>
  )
}
